import './global.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";

function initSentry() {
  if (process.env.REACT_APP_SENTRY_LOG === 'true') {
    console.log('Initializing sentry...')
    Sentry.init({
      dsn: "https://2a80d72e6d91640609177e5f430fa39c@o4510119094779904.ingest.us.sentry.io/4510119150223360",
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration()
      ],
      environment: process.env.NODE_ENV,
      release: process.env.REACT_APP_VERSION,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [],
      beforeSend(event, hint) {
        if (event.request?.url?.includes("/auth")) return null;
        return event;
      },
    });

    if (Sentry.isInitialized()) {
      console.log('Sentry initialized.')
      console.log(`App version ${process.env.REACT_APP_VERSION}`)
    } else {
      console.log('Failed to initialize sentry.')
    }
  }
}

initSentry();

import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { mobileRedirectTarget } from './utils/mobileHost';

// Force phone/tablet visits on the desktop hosts (idarah.io, www.idarah.io) to
// m.idarah.io before the app renders: field users should land on the mobile
// shell and install from the m.idarah.io origin. Excluded by mobileRedirectTarget:
// localhost, staging, m.idarah.io itself. Path and query are preserved so deep
// links and magic-login tokens survive the move.
(function enforceMobileHostRedirect() {
  const target = mobileRedirectTarget(
    window.location.hostname,
    window.location.pathname,
    window.location.search,
  );
  // <1024 matches the phone/tablet breakpoint used across the mobile UI.
  if (target && window.innerWidth < 1024 && target !== window.location.href) {
    window.location.replace(target);
  }
})();

const container = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(container);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

/**
 * Take the splash (public/index.html) down once the app has actually painted.
 *
 * Waiting for `#root` to gain its first element — rather than a timer or a rAF after
 * render — also covers the session check: AuthProvider renders nothing while `loading`
 * is true, so #root stays empty and the splash stays up instead of revealing a white
 * page. A boot that never paints keeps the splash, which is the honest thing to show.
 */
const splash = document.getElementById('splash');
if (splash) {
  const dismissSplash = () => {
    splash.classList.add('splash-hidden');
    splash.addEventListener('transitionend', () => splash.remove(), { once: true });
    // transitionend never fires if the tab is hidden mid-fade, or when the fade is
    // skipped; removing twice is harmless.
    window.setTimeout(() => splash.remove(), 500);
  };

  const splashObserver = new MutationObserver(() => {
    if (!container.firstElementChild) return;
    splashObserver.disconnect();
    dismissSplash();
  });
  splashObserver.observe(container, { childList: true });
}

if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).catch(() => {
      // Non-fatal: app stays fully functional online without the service worker.
    });
  });
}
